import { useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Divider, InlineStack, Link, Select, Text } from '@customerio/pluma-components/react';
import type { SelectProps } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { fieldError } from '@customerio/dryad';
import { getSegmentsQueryOptions } from '@/services/db/segment/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';

/** The editing state a segment-membership leaf holds. */
export type SegmentsConditionState = {
  segmentIds: readonly (number | string)[];
  negate: boolean;
};

type SegmentsRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's segments leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /**
   * Coerces a picked option's string id to the type this surface's wire uses.
   * Ids arrive as strings from the picker but producers write numbers, and the
   * saved form has to round-trip unchanged.
   */
  toSegmentId: (value: string) => number | string;
  /**
   * Opens the surface's create-segment flow for this row, or undefined where
   * the shortcut isn't offered — the picker then leaves the entry out.
   */
  onCreateSegment?: (nodeId: string) => void;
  /** The wording for the membership select, which reads differently per surface. */
  membershipWording?: { in: string; notIn: string };
};

type SegmentOption = { value: string; label: string; icon: 'segment-manual' | 'segment-data-driven' };

const DEFAULT_MEMBERSHIP_WORDING = { in: 'in', notIn: 'not in' };

// Pluma renders this above the option list, below the search input. Its props
// come from the Select's own slot type, so the unexported select-context shape
// never has to be restated here.
type BeforeOptionsProps = ComponentProps<NonNullable<SelectProps['beforeOptionsComponent']>>;

const createSegmentLinkStyle = {
  borderRadius: 0,
  outlineOffset: `calc(${themeVars.border['width-025']} * -1)`,
};

type CreateSegmentEntryProps = BeforeOptionsProps & {
  onCreateSegment: () => void;
};

/** The picker's "create a new segment" entry, above the segment options. */
function CreateSegmentEntry({ selectContext, onCreateSegment }: CreateSegmentEntryProps) {
  return (
    <Text display="flex" flexDirection="column" size="sm">
      <Link
        data-testid="segments-create-new"
        onClick={() => {
          // Close the dropdown before the form opens, so the menu isn't left
          // floating over it.
          selectContext.floatingContext.onOpenChange(false);
          onCreateSegment();
        }}
        px="100"
        py="050"
        flexGrow="1"
        textAlign="left"
        style={createSegmentLinkStyle}
      >
        Create a new data-driven segment
      </Link>
      <Divider />
    </Text>
  );
}

/** A segment-membership condition row: [type] [in / not in] [segments…]. */
export function SegmentsRow({
  id,
  conditionType,
  typeSelect,
  toSegmentId,
  onCreateSegment,
  membershipWording = DEFAULT_MEMBERSHIP_WORDING,
}: SegmentsRowProps) {
  const workspaceId = requireWorkspaceId();
  const queryClient = useQueryClient();
  const meta = useMeta();
  const { state, node } = useLeafState<SegmentsConditionState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  // "Select at least one segment." renders only once the picker has been
  // visited (closing its dropdown) — or from the start when the row mounted
  // with segments already chosen — so a freshly added row doesn't open
  // covered in red.
  const [segmentsTouched, setSegmentsTouched] = useState(() => state.segmentIds.length > 0);
  const segmentsErrorMessage = segmentsTouched ? fieldError(errors, 'segmentIds') : undefined;

  const negateOptions = useMemo(
    () => [
      { value: 'false', label: membershipWording.in },
      { value: 'true', label: membershipWording.notIn },
    ],
    [membershipWording],
  );

  const segmentsQuery = useQuery(getSegmentsQueryOptions(workspaceId, queryClient));

  const segmentOptions: SegmentOption[] = useMemo(
    () =>
      (segmentsQuery.data?.segments ?? []).map((segment) => ({
        value: String(segment.id),
        label: segment.name,
        icon: segment.type === 'static' ? 'segment-manual' : 'segment-data-driven',
      })),
    [segmentsQuery.data],
  );

  // Resolved against the loaded list; a saved condition referencing a segment
  // that hasn't loaded (or no longer exists) still renders a stable entry
  // rather than dropping out of the selection.
  const selectedOptions: SegmentOption[] = useMemo(() => {
    const loaded = new Map(segmentOptions.map((option) => [option.value, option]));
    return state.segmentIds.map((segmentId) => {
      const stringId = String(segmentId);
      return loaded.get(stringId) ?? { value: stringId, label: stringId, icon: 'segment-manual' };
    });
  }, [segmentOptions, state.segmentIds]);

  // Absent when the shortcut isn't offered, which leaves the entry out of the
  // picker entirely. Memoized so the open dropdown isn't remounted per render.
  const beforeOptionsComponent = useMemo(
    () =>
      onCreateSegment == null
        ? undefined
        : function SegmentsCreateEntry(props: BeforeOptionsProps) {
            return <CreateSegmentEntry {...props} onCreateSegment={() => onCreateSegment(id)} />;
          },
    [onCreateSegment, id],
  );

  const handleSegmentsChange = (values: string[] | null) => {
    if (node == null) {
      return;
    }
    // Ids already in the state keep their parsed type so a round-trip stays
    // byte-identical; newly picked ones are coerced to numbers, which is what
    // every producer of this wire writes.
    const previousById = new Map(state.segmentIds.map((segmentId) => [String(segmentId), segmentId]));
    const segmentIds = (values ?? []).map((value) => previousById.get(value) ?? toSegmentId(value));
    updateState(node, { segmentIds });
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-negate-select"
        ariaLabel="Segment membership"
        size="sm"
        options={negateOptions}
        value={state.negate ? 'true' : 'false'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { negate: value === 'true' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Select
        data-testid="dryad-segments-select"
        ariaLabel="Choose segments"
        size="sm"
        value={selectedOptions}
        options={segmentOptions}
        error={segmentsErrorMessage}
        onChange={handleSegmentsChange}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setSegmentsTouched(true);
          }
        }}
        isDisabled={meta.isDisabled}
        isMulti
        isSearchable
        canWrap
        placeholder="Segments"
        searchPlaceholder="Search segments…"
        isLoading={segmentsQuery.isLoading}
        withVirtualizer
        beforeOptionsComponent={beforeOptionsComponent}
      />
    </InlineStack>
  );
}
